import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Car, Loader2, ShieldCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { IMAGES } from "@/lib/constants";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      if (!["admin", "super_admin", "agent", "support"].includes(u.role)) {
        setError("This portal is for staff accounts only.");
        setLoading(false);
        return;
      }
      toast.success(`Welcome, ${u.name}`);
      navigate(["agent", "support"].includes(u.role) ? "/agent" : "/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed. Check your credentials.");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-navy">
      <div className="hidden w-1/2 flex-col justify-between p-12 lg:flex">
        <Link to="/" className="flex items-center gap-2"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white"><Car size={18} /></span><span className="font-heading text-lg font-bold text-white">SmartFares<span className="text-brand">4U</span></span></Link>
        <div className="relative flex-1">
          <img src={IMAGES.mercedes} alt="" className="absolute inset-0 my-8 h-[70%] w-full rounded-3xl object-cover opacity-30" />
          <div className="relative flex h-full flex-col justify-center">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white"><ShieldCheck size={14} className="text-emerald-400" /> Operations Portal</span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-white">Admin & Agent Console</h2>
            <p className="mt-2 max-w-sm text-slate-300">Manage bookings, drivers, support chats, tickets and the entire SmartFares4U operation from one secure dashboard.</p>
          </div>
        </div>
        <p className="text-xs text-slate-500">Role-based access · Secure session · Activity logging</p>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Lock size={20} /></span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-navy">Admin Login</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to the operations console.</p>
          {forgot ? (
            <div className="mt-6 space-y-4">
              <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
              <Button type="button" className="w-full bg-brand hover:bg-brand/90" onClick={() => { toast.success("If this email exists, a reset link has been sent."); setForgot(false); }} data-testid="send-reset">Send reset link</Button>
              <button type="button" onClick={() => setForgot(false)} className="w-full text-sm text-brand">Back to login</button>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" required /></div>
              <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" required /></div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-me" /> Remember me</label>
                <button type="button" onClick={() => setForgot(true)} className="text-sm font-medium text-brand" data-testid="forgot-password">Forgot password?</button>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
              <Button type="submit" className="w-full bg-brand hover:bg-brand/90" disabled={loading} data-testid="admin-login-submit">{loading ? <Loader2 size={16} className="animate-spin" /> : "Login"}</Button>
            </div>
          )}
          <p className="mt-4 text-center text-xs text-slate-400">Not an admin? <Link to="/login" className="font-semibold text-brand">Customer login</Link></p>
        </form>
      </div>
    </div>
  );
}
